import { useState, useEffect, useCallback } from "react";
import { appConfirm } from "../../../components/ConfirmDialog";
import { api } from "../../../lib/api";
import { toast } from "sonner";
import { CheckCircle2, Clock, Image as ImageIcon, ClipboardList, Loader2, ScrollText, Pencil, Save, RotateCcw, FlipHorizontal, Printer, Send, Zap, ExternalLink } from "lucide-react";
import { ImageLightbox } from "./shared";

const gbp = (n) => "£" + Math.round(Number(n || 0)).toLocaleString("en-GB");
const TILED_DESC = "Tiled roof (confirmed at survey)";

const autoScopeToText = (sections) => (sections || [])
  .map((s) => s.title + "\n" + (s.items || []).map((i) => "\u2022 " + i).join("\n"))
  .join("\n\n");

const RENDER_LABELS = { exterior: "Exterior", interior: "Interior", opening: "House / extension opening", bathroom: "Bathroom / WC" };

export function Job({ reference, canManage, reload }) {
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [tiledCost, setTiledCost] = useState("");
  const [savingRoof, setSavingRoof] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [editScope, setEditScope] = useState(false);
  const [scopeText, setScopeText] = useState("");
  const [savingScope, setSavingScope] = useState(false);
  const [bust, setBust] = useState(Date.now());
  const [flipping, setFlipping] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/booking/${reference}/job`);
      setJob(r.data);
      const t = (r.data.custom_line_items || []).find((i) => i.description === TILED_DESC);
      setTiledCost(t ? String(t.unit_price ?? t.amount ?? "") : "");
    } catch { toast.error("Could not load job details"); }
    setLoading(false);
  }, [reference]);
  useEffect(() => { load(); }, [load]);

  const startEditScope = () => {
    const s = job.scope || {};
    setScopeText(s.custom || autoScopeToText(s.auto));
    setEditScope(true);
  };
  const saveScope = async () => {
    setSavingScope(true);
    try {
      await api.put(`/admin/booking/${reference}/scope`, { scope: scopeText });
      toast.success("Scope of works saved");
      setEditScope(false); await load();
    } catch { toast.error("Could not save scope"); }
    setSavingScope(false);
  };
  const resetScope = async () => {
    if (!(await appConfirm({ title: "Reset scope of works?", description: "Reset the scope of works back to the auto-generated version?", confirmLabel: "Reset" }))) return;
    setSavingScope(true);
    try {
      await api.put(`/admin/booking/${reference}/scope`, { scope: null });
      toast.success("Scope reset to auto-generated");
      setEditScope(false); await load();
    } catch { toast.error("Could not reset scope"); }
    setSavingScope(false);
  };

  const flipRender = async (kind) => {
    setFlipping(kind);
    try {
      await api.post(`/admin/booking/${reference}/render/${kind}/flip`);
      setBust(Date.now());
      toast.success("Image flipped");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not flip image"); }
    setFlipping(null);
  };
  const bustUrl = (u) => u ? u + (u.includes("?") ? "&" : "?") + "t=" + bust : u;

  const [sendingScope, setSendingScope] = useState(false);
  const sendScope = async () => {
    if (!(await appConfirm({ title: "Email the scope of works?", description: "Send the scope of works to the client on file?", confirmLabel: "Send" }))) return;
    setSendingScope(true);
    try {
      const r = await api.post(`/admin/booking/${reference}/scope/send`);
      toast.success(r.data.sent ? `Scope sent to ${r.data.to}` : "Scope logged (email not sent — check Gmail connection)");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send scope"); }
    setSendingScope(false);
  };
  const printScope = () => {
    const s = job.scope || {};
    const w = window.open("", "_blank");
    if (!w) return;
    const doc = w.document;
    doc.title = `Scope of works — ${reference}`;
    const style = doc.createElement("style");
    style.textContent = "body{font-family:Georgia,serif;max-width:760px;margin:32px auto;padding:0 24px;color:#1f2937;line-height:1.6}h1{font-size:22px}h3{color:#12263A;margin:18px 0 4px}ul{margin:0 0 8px 0;padding-left:20px}@media print{@page{margin:16mm}}";
    doc.head.appendChild(style);
    const h1 = doc.createElement("h1");
    h1.textContent = `Scope of works — ${reference}`;
    doc.body.appendChild(h1);
    const sub = doc.createElement("div");
    sub.style.cssText = "color:#6b7280;font-size:13px;margin-bottom:12px";
    sub.textContent = job.config?.width ? `The Extension Works · ${job.config.width}m x ${job.config.depth}m rear extension` : "The Extension Works";
    doc.body.appendChild(sub);
    if (s.custom) {
      const div = doc.createElement("div");
      div.style.whiteSpace = "pre-wrap";
      div.textContent = s.custom;
      doc.body.appendChild(div);
    } else {
      (s.auto || []).forEach((sec) => {
        const h3 = doc.createElement("h3");
        h3.textContent = sec.title;
        doc.body.appendChild(h3);
        const ul = doc.createElement("ul");
        (sec.items || []).forEach((i) => {
          const li = doc.createElement("li");
          li.textContent = i;
          ul.appendChild(li);
        });
        doc.body.appendChild(ul);
      });
    }
    w.focus();
    setTimeout(() => w.print(), 300);
  };

  const confirmJob = async () => {
    setConfirming(true);
    try {
      await api.post(`/admin/booking/${reference}/confirm-job`);
      toast.success("Job confirmed — approved quotation filed to Documents");
      await load(); reload && reload();
    } catch { toast.error("Could not confirm the job"); }
    setConfirming(false);
  };

  const saveTiledRoof = async () => {
    const cost = parseFloat(tiledCost);
    if (isNaN(cost) || cost < 0) { toast.error("Enter a valid amount"); return; }
    setSavingRoof(true);
    try {
      const keep = (job.custom_line_items || []).filter((i) => i.description !== TILED_DESC)
        .map((i) => ({ id: i.id, description: i.description, qty: i.qty ?? 1, unit_price: i.unit_price ?? i.amount, discount: i.discount || null }));
      if (cost > 0) keep.push({ description: TILED_DESC, qty: 1, unit_price: cost });
      await api.put(`/admin/booking/${reference}/line-items`, { items: keep });
      toast.success("Tiled roof cost saved to the contract total");
      await load(); reload && reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    setSavingRoof(false);
  };

  if (loading) return <div data-testid="jobfile-job" className="py-10 text-center text-sm text-[#6B7280]">Loading job…</div>;
  if (!job) return <div data-testid="jobfile-job" />;

  const t = job.totals || {};
  const stat = [["Total", gbp(t.total)], ["Deposit", gbp(t.deposit)], ["Paid to date", gbp(t.paid)], ["Balance", gbp(t.balance)]];

  return (
    <div data-testid="jobfile-job" className="space-y-6">
      {/* Confirm banner */}
      <div className={`border p-4 flex items-start justify-between gap-3 flex-wrap ${job.job_confirmed ? "border-[#1F6B3B] bg-[#F1F8F3]" : "border-[#E5E7EB] bg-white"}`}>
        <div className="flex items-start gap-2.5">
          {job.job_confirmed ? <CheckCircle2 size={20} className="text-[#1F6B3B] mt-0.5" /> : <Clock size={20} className="text-[#C79A3B] mt-0.5" />}
          <div>
            <div className="font-black text-[#12263A] flex items-center gap-2 flex-wrap">
              {job.job_confirmed ? "Job confirmed" : "Awaiting confirmation"}
              {job.booking_fee_bypassed ? (
                <span data-testid="booking-fee-badge-waived" className="text-[10px] font-bold uppercase tracking-widest bg-[#C79A3B] text-white px-2 py-0.5">£499 waived{job.booking_fee_bypassed_by ? ` by ${job.booking_fee_bypassed_by.split("@")[0]}` : ""}</span>
              ) : (t.paid || 0) >= 499 ? (
                <span data-testid="booking-fee-badge-paid" className="text-[10px] font-bold uppercase tracking-widest bg-[#1F6B3B] text-white px-2 py-0.5">£499 paid</span>
              ) : (
                <span data-testid="booking-fee-badge-pending" className="text-[10px] font-bold uppercase tracking-widest bg-[#6B7280] text-white px-2 py-0.5">£499 pending</span>
              )}
            </div>
            <p className="text-xs text-[#6B7280] mt-0.5 max-w-md">
              {job.job_confirmed
                ? `Confirmed${job.job_confirmed_at ? " on " + new Date(job.job_confirmed_at).toLocaleDateString("en-GB") : ""}. The approved quotation is filed in the Documents tab.`
                : "Confirm the job after the survey. This files the approved quotation into the client's Documents."}
            </p>
          </div>
        </div>
        {canManage && (
          <button data-testid="job-confirm-btn" onClick={confirmJob} disabled={confirming}
            className={`inline-flex items-center gap-1.5 px-4 py-2 text-xs font-bold uppercase tracking-widest text-white disabled:opacity-60 ${job.job_confirmed ? "bg-[#12263A]" : "bg-[#1F6B3B]"}`}>
            {confirming ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
            {job.job_confirmed ? "Re-file quotation" : "Confirm job"}
          </button>
        )}
      </div>

      {/* Totals */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {stat.map(([k, v]) => (
          <div key={k} className="border border-[#E5E7EB] bg-white p-2.5">
            <div className="label-eyebrow">{k}</div>
            <div className="text-sm font-bold text-[#12263A]">{v}</div>
          </div>
        ))}
      </div>

      {/* Generated visuals */}
      <div>
        <div className="label-eyebrow mb-2 flex items-center gap-1.5"><ImageIcon size={13} /> Generated design visuals</div>
        {(() => {
          const kinds = ["exterior", "interior", "opening", "bathroom"].filter((k) => job.renders?.[k]);
          if (kinds.length === 0) {
            return <div className="py-6 text-center text-sm text-[#9CA3AF] border border-dashed border-[#E5E7EB]">No visuals generated for this job yet.</div>;
          }
          return (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {kinds.map((k) => (
                <figure key={k} className="relative group">
                  <img data-testid={`job-render-${k}`} src={bustUrl(job.renders[k])} alt={RENDER_LABELS[k]}
                    onClick={() => setLightbox({ src: bustUrl(job.renders[k]), title: RENDER_LABELS[k], filename: `${k}-${reference}.png` })}
                    onError={(e) => { const f = e.currentTarget.closest("figure"); if (f) f.style.display = "none"; }}
                    className="w-full border border-[#E5E7EB] bg-[#F8F6F2] cursor-zoom-in" />
                  {canManage && (
                    <button data-testid={`job-render-flip-${k}`} onClick={() => flipRender(k)} disabled={flipping === k}
                      title="Flip horizontally"
                      className="absolute top-2 right-2 bg-white/95 border border-[#E5E7EB] p-1.5 hover:text-[#C79A3B] opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-60">
                      {flipping === k ? <Loader2 size={14} className="animate-spin" /> : <FlipHorizontal size={14} />}
                    </button>
                  )}
                  <figcaption className="text-[11px] text-[#6B7280] mt-0.5">{RENDER_LABELS[k]}</figcaption>
                </figure>
              ))}
            </div>
          );
        })()}
        {canManage && (job.renders?.exterior || job.renders?.interior) && (
          <p className="text-[11px] text-[#9CA3AF] mt-1">Tip: if the interior looks left-right mirrored versus the exterior, hover an image and click the flip icon to correct it instantly.</p>
        )}
      </div>

      {/* Scope of works */}
      <div>
        <div className="label-eyebrow mb-2 flex items-center justify-between gap-2">
          <span className="flex items-center gap-1.5"><ScrollText size={13} /> Scope of works</span>
          {!editScope && (
            <span className="flex items-center gap-3">
              <button data-testid="scope-print-btn" onClick={printScope} className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-[#6B7280] hover:text-[#12263A]">
                <Printer size={12} /> Print
              </button>
              {canManage && (
                <button data-testid="scope-send-btn" onClick={sendScope} disabled={sendingScope} className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-[#6B7280] hover:text-[#12263A] disabled:opacity-60" title="Emails the scope of works to the client">
                  {sendingScope ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Send to client <Zap size={10} className="fill-[#F59E0B] text-[#B45309]" />
                </button>
              )}
              {canManage && job.scope?.custom && (
                <button data-testid="scope-reset-display-btn" onClick={resetScope} className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-[#6B7280] hover:text-[#C2401F]">
                  <RotateCcw size={12} /> Reset to auto
                </button>
              )}
              {canManage && (
                <a data-testid="scope-configurator-btn"
                  href={(job.quote_id && job.route === "pd"
                    ? `/quote?edit=${encodeURIComponent(job.quote_id)}`
                    : `/quote?postcode=${encodeURIComponent(job.postcode || "")}` +
                      `&name=${encodeURIComponent(job.name || "")}` +
                      `&email=${encodeURIComponent(job.email || "")}` +
                      `&phone=${encodeURIComponent(job.phone || "")}`) +
                    `&link_booking=${encodeURIComponent(reference)}`}
                  target="_blank" rel="noreferrer"
                  title="Opens the customer configurator prefilled with this client's details — works for out-of-area postcodes when you're signed in as staff. The revised quote will be auto-linked to this job on save."
                  className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-[#C79A3B] hover:underline">
                  <ExternalLink size={12} /> Build in configurator (as customer)
                </a>
              )}
              {canManage && (
                <button data-testid="scope-edit-btn" onClick={startEditScope} className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-[#C79A3B] hover:underline">
                  <Pencil size={12} /> Edit
                </button>
              )}
            </span>
          )}
        </div>
        {editScope ? (
          <div className="border border-[#E5E7EB] bg-white p-3">
            <textarea data-testid="scope-textarea" value={scopeText} onChange={(e) => setScopeText(e.target.value)} rows={16}
              className="w-full border border-[#E5E7EB] px-3 py-2 text-sm font-mono leading-relaxed" />
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              <button data-testid="scope-save-btn" onClick={saveScope} disabled={savingScope} className="de-btn-primary text-sm disabled:opacity-60">
                {savingScope ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save scope
              </button>
              <button data-testid="scope-cancel-btn" onClick={() => setEditScope(false)} className="de-btn-secondary text-sm">Cancel</button>
              {job.scope?.custom && (
                <button data-testid="scope-reset-btn" onClick={resetScope} disabled={savingScope} className="inline-flex items-center gap-1 text-xs text-[#6B7280] hover:text-[#C2401F] ml-auto">
                  <RotateCcw size={13} /> Reset to auto
                </button>
              )}
            </div>
          </div>
        ) : (
          <div data-testid="scope-display" className="border border-[#E5E7EB] bg-white p-4">
            {job.scope?.custom ? (
              <div className="text-sm text-[#374151] whitespace-pre-wrap leading-relaxed">{job.scope.custom}</div>
            ) : (
              <div className="space-y-3">
                {(job.scope?.auto || []).map((s, i) => (
                  <div key={`${s.title}-${i}`} data-testid={`scope-section-${i}`}>
                    <div className="font-bold text-sm text-[#12263A]">{s.title}</div>
                    <ul className="list-disc pl-5 text-sm text-[#374151] space-y-0.5 mt-1">
                      {s.items.map((it, j) => <li key={`${j}-${it}`}>{it}</li>)}
                    </ul>
                  </div>
                ))}
              </div>
            )}
            <p className="text-[11px] text-[#9CA3AF] mt-3">
              {job.scope?.custom
                ? `Customised${job.scope.edited_by ? " by " + job.scope.edited_by : ""}. Edit to amend.`
                : "Auto-generated from the approved design. Click Edit to customise."}
            </p>
          </div>
        )}
      </div>

      {/* Approved selections / spec */}
      <div>
        <div className="label-eyebrow mb-2 flex items-center gap-1.5"><ClipboardList size={13} /> Approved design &amp; selections</div>
        <div className="border border-[#E5E7EB] bg-white divide-y divide-[#F0EEE9]">
          {(job.spec || []).map(([label, value], i) => (
            <div key={`${label}-${i}`} data-testid={`job-spec-${i}`} className="flex gap-3 px-3 py-2 text-sm">
              <span className="w-40 shrink-0 font-bold text-[#12263A]">{label}</span>
              <span className="text-[#6B7280]">{value}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Tiled roof confirmed cost */}
      {job.roof === "tiled_pitched" && canManage && (
        <div data-testid="job-tiled-roof" className="border border-[#C79A3B] bg-[#FFFDF8] p-4">
          <div className="font-bold text-[#12263A]">Tiled roof — confirmed cost</div>
          <p className="text-xs text-[#6B7280] mt-0.5 mb-2 max-w-md">The pitched-roof tile covering is confirmed at survey. Enter the confirmed cost to add it to the contract total (appears on the invoice).</p>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[#12263A] font-bold">£</span>
            <input data-testid="job-tiled-roof-input" type="number" step="1" min="0" value={tiledCost}
              onChange={(e) => setTiledCost(e.target.value)} placeholder="e.g. 6500"
              className="border border-[#E5E7EB] px-3 py-2 text-sm w-40" />
            <button data-testid="job-tiled-roof-save" onClick={saveTiledRoof} disabled={savingRoof}
              className="de-btn-primary disabled:opacity-60">{savingRoof ? <Loader2 size={16} className="animate-spin" /> : null} Save to contract</button>
          </div>
        </div>
      )}

      {lightbox && <ImageLightbox {...lightbox} onClose={() => setLightbox(null)} />}
    </div>
  );
}
